import { Link } from "react-router-dom";
import { useAsync } from "../hooks/useAsync";
import { getDeload, getFatigue, getReadiness, listSessions } from "../api/endpoints";
import { MUSCLE_GROUP_LABELS, type ReadinessSignal } from "../api/types";
import { ErrorMessage, Spinner } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import Icon from "../components/Icon";

/**
 * Ported from the C# dashboard (Views/Home/Index.cshtml), Load & Flow branch —
 * the sections gated on `Brand.IsStrengthFirst`, plus the quick-start block
 * that the running-first branch replaced. Race and "today's plan" cards belong
 * to Hillory and are not part of this app.
 */

const CARD =
  "bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-sm";
const SECTION_HEADING =
  "text-heading-md text-gray-900 dark:text-textDark-primary pl-3 border-l-2 border-emerald-500 dark:border-emerald-400";

/**
 * Readiness is an advisory signal (LF-14), not a prescription. Colours follow
 * the house rule: emerald for good, amber for caution, never red.
 */
const READINESS_BADGE: Record<ReadinessSignal, string> = {
  green: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300",
  amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
  gray: "bg-gray-100 dark:bg-dark-border text-gray-600 dark:text-textDark-muted",
};

const READINESS_LABEL: Record<ReadinessSignal, string> = {
  green: "Ready",
  amber: "Caution",
  gray: "Take it easy",
};

/** Thresholds copied from DashboardViewModel.GetFatigueColor. */
function fatigueColour(level: number): string {
  if (level < 0.3) return "bg-emerald-500";
  if (level < 0.6) return "bg-amber-400";
  return "bg-amber-600";
}

/** Matches the inline greeting script in the Razor view. */
function greeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function TodayPage() {
  const { user } = useAuth();
  const fatigue = useAsync(getFatigue, []);
  const deload = useAsync(getDeload, []);
  const readiness = useAsync(getReadiness, []);
  const sessions = useAsync(listSessions, []);

  const recent = sessions.data?.items?.slice(0, 5) ?? [];
  const name = user?.displayName ?? user?.email ?? "";

  return (
    <div className="space-y-6" data-testid="today-page">
      <div>
        <h1
          className="text-heading-xl text-gray-900 dark:text-textDark-primary"
          data-testid="dashboard-welcome"
        >
          {greeting(new Date().getHours())}
          {name ? `, ${name}` : ""}
        </h1>
        <p className="text-sm text-gray-500 dark:text-textDark-muted mt-1">
          Here's your training overview.
        </p>
      </div>

      <div className={`${CARD} p-6`} data-testid="direction-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-gray-500 dark:text-textDark-muted uppercase tracking-wide">
            Today's Direction
          </h2>
          {readiness.data && (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${READINESS_BADGE[readiness.data.signal]}`}
              data-testid="direction-intent-badge"
            >
              {READINESS_LABEL[readiness.data.signal]}
            </span>
          )}
        </div>
        {readiness.loading && <Spinner />}
        {readiness.error && <ErrorMessage error={readiness.error} onRetry={readiness.reload} />}
        {readiness.data && (
          <div data-testid="today-readiness">
            <p className="text-sm text-gray-600 dark:text-textDark-secondary">
              <span data-testid="today-readiness-score">{readiness.data.score}</span>
              {" / 100 readiness — "}
              {readiness.data.weeklyStrengthCount} sessions this week
            </p>
            <p className="mt-2 text-xs text-gray-400 dark:text-textDark-muted">
              Advisory only — this doesn't change what the planner prescribes.
            </p>
          </div>
        )}
      </div>

      {deload.data?.isDeloading && (
        <div
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 shadow-sm"
          data-testid="today-deload"
        >
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Recovery week in progress
          </p>
          {deload.data.message && (
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">{deload.data.message}</p>
          )}
        </div>
      )}

      <div
        className={`flex flex-wrap items-center gap-3 ${CARD} p-4`}
        data-testid="dashboard-quick-nav"
      >
        <Link
          to="/sessions/plan"
          className="rounded-lg font-medium px-5 py-2.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-all active:scale-[0.98] inline-flex items-center gap-2 shadow-sm"
          data-testid="dashboard-quick-start-button"
        >
          <Icon name="lightning" size="sm" className="text-white" />
          Quick Start
        </Link>
        <Link
          to="/sessions"
          className="rounded-lg font-medium px-4 py-2 text-sm text-gray-700 dark:text-textDark-secondary border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-elevated transition-all active:scale-[0.98] inline-flex items-center gap-2"
          data-testid="dashboard-plan-button"
        >
          <Icon name="clipboard-task" size="sm" />
          Plan &amp; Review
        </Link>
        <span className="text-xs text-gray-400 dark:text-textDark-muted ml-auto hidden sm:inline">
          Get a personalised suggestion based on recovery
        </span>
      </div>

      <div className={`${CARD} p-6`} data-testid="dashboard-fatigue-summary">
        <h2 className={`${SECTION_HEADING} mb-5`}>Muscle Fatigue</h2>
        {fatigue.loading && <Spinner />}
        {fatigue.error && <ErrorMessage error={fatigue.error} onRetry={fatigue.reload} />}
        {fatigue.data && (
          <div className="space-y-3" data-testid="today-fatigue">
            {fatigue.data.muscleGroups.map((m) => {
              const pct = Math.round(Math.min(Math.max(m.fatigueLevel, 0), 1) * 100);
              return (
                <div
                  key={m.muscleGroup}
                  className="flex items-center gap-4"
                  data-testid={`dashboard-fatigue-${m.muscleGroup}`}
                >
                  <span className="text-xs font-medium text-gray-500 dark:text-textDark-muted w-24 shrink-0">
                    {MUSCLE_GROUP_LABELS[m.muscleGroup] ?? m.muscleGroup}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-dark-elevated overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${fatigueColour(m.fatigueLevel)}`}
                      style={{ width: `${pct}%` }}
                      data-testid={`dashboard-fatigue-${m.muscleGroup}-fill`}
                    />
                  </div>
                  <span className="text-xs tabular-nums text-gray-400 dark:text-textDark-muted w-8 text-right">
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={CARD} data-testid="dashboard-recent-sessions">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className={SECTION_HEADING}>Recent Sessions</h2>
          {recent.length > 0 && (
            <Link
              to="/sessions"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
              data-testid="dashboard-view-all-sessions"
            >
              View all
            </Link>
          )}
        </div>

        {sessions.loading && (
          <div className="px-6 py-10">
            <Spinner />
          </div>
        )}
        {sessions.error && (
          <div className="px-6 py-6">
            <ErrorMessage error={sessions.error} onRetry={sessions.reload} />
          </div>
        )}

        {sessions.data && recent.length === 0 && (
          <div className="px-6 py-10 text-center" data-testid="today-no-sessions">
            <Icon
              name="clock"
              size="xl"
              className="text-gray-300 dark:text-textDark-muted mx-auto mb-3"
            />
            <p className="text-sm text-gray-500 dark:text-textDark-muted">
              No sessions yet. Plan your first session to get started!
            </p>
          </div>
        )}

        {recent.length > 0 && (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {recent.map((s, i) => (
              <Link
                key={s.id}
                to={`/sessions/${s.id}`}
                data-testid={`dashboard-session-card-${i}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors"
              >
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-textDark-primary truncate">
                    {s.sessionName ?? "Training Session"}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-textDark-muted mt-0.5">
                    {s.exerciseCount ?? 0} exercises · {s.rounds} rounds
                  </p>
                </div>
                <div className="text-right shrink-0 ml-4">
                  {s.completedAt && (
                    <span className="text-xs text-gray-400 dark:text-textDark-muted">
                      {new Date(s.completedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
